import { motion } from "framer-motion";
import { Check, Minus, Star, ShoppingBag } from "lucide-react";
import { useCart } from "./Cart";
import { optimizedImage } from "@/lib/image";

export default function EditionsComparison({ data }) {
  const { addItem } = useCart();
  if (!data || !data.editions?.length) return null;

  const editions = data.editions;

  return (
    <section
      id="editions"
      data-testid="editions-comparison"
      className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-white/5 overflow-hidden"
    >
      {/* Key art background */}
      {data.backgroundImage && (
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          <img
            src={optimizedImage(data.backgroundImage, { width: 1920 })}
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black via-black/85 to-black" />
          <div className="absolute inset-0 bg-black/45" />
        </div>
      )}

      {/* Ambient glow */}
      <div className="absolute -top-40 right-1/4 w-[500px] h-[500px] rounded-full bg-accent/[0.05] blur-3xl pointer-events-none" />

      <div className="max-w-[1400px] mx-auto relative">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="mb-14 md:mb-20 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end"
        >
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-12 h-px bg-accent" />
              <span className="font-body text-[11px] uppercase tracking-[0.35em] text-accent">
                {data.eyebrow}
              </span>
            </div>
            <h2
              data-testid="editions-title"
              className="font-heading uppercase text-white text-5xl md:text-6xl lg:text-7xl leading-[0.9]"
            >
              {data.title}
            </h2>
          </div>
          <p className="lg:col-span-5 font-body text-base md:text-lg text-white/60 leading-relaxed">
            {data.description}
          </p>
        </motion.div>

        {/* Desktop grid */}
        <div
          className="hidden md:grid grid-cols-[minmax(220px,1.4fr)_repeat(3,minmax(0,1fr))] border border-white/10"
          data-testid="editions-table"
        >
          {/* Column header row */}
          <div className="border-b border-white/10 p-6 bg-[#0F0F12]">
            <span className="font-body text-[10px] uppercase tracking-[0.35em] text-white/40">
              Confronto contenuti
            </span>
          </div>
          {editions.map((ed, i) => (
            <motion.div
              key={ed.id}
              data-testid={`edition-header-${ed.id}`}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 + i * 0.08 }}
              className={`relative border-b border-l border-white/10 p-6 flex flex-col gap-3 ${
                ed.featured
                  ? "bg-accent/[0.06]"
                  : "bg-[#0F0F12]"
              }`}
            >
              {ed.featured && (
                <div className="absolute -top-3 left-4 flex items-center gap-1.5 bg-accent text-black px-3 py-1">
                  <Star size={10} />
                  <span className="font-body text-[9px] uppercase tracking-[0.3em] font-bold">
                    Consigliata
                  </span>
                </div>
              )}
              <div className="flex items-center justify-between gap-3">
                <span className="font-heading text-xl md:text-2xl uppercase text-white tracking-[0.06em]">
                  {ed.name}
                </span>
                {ed.tag && (
                  <span className="font-body text-[9px] uppercase tracking-[0.25em] text-white/50 border border-white/15 px-2 py-1 shrink-0">
                    {ed.tag}
                  </span>
                )}
              </div>
              <div
                data-testid={`edition-price-${ed.id}`}
                className={`font-heading text-3xl md:text-4xl leading-none ${
                  ed.featured ? "text-accent" : "text-white"
                }`}
              >
                {ed.price}
              </div>
              <button
                type="button"
                data-testid={`edition-add-${ed.id}`}
                onClick={() =>
                  addItem({
                    id: `edition-${ed.id}`,
                    name: `${ed.name} · The Protocol Lite`,
                    price: ed.price,
                  })
                }
                className={`mt-2 inline-flex items-center justify-center gap-2 px-4 py-2.5 transition-colors ${
                  ed.featured
                    ? "bg-accent text-black hover:bg-accent/90"
                    : "border border-accent/40 text-accent hover:bg-accent/10"
                }`}
              >
                <ShoppingBag size={12} />
                <span className="font-heading text-xs tracking-[0.15em] uppercase">
                  Aggiungi
                </span>
              </button>
            </motion.div>
          ))}

          {/* Feature rows */}
          {data.features.map((feat, rowIdx) => (
            <div className="contents" key={feat.id}>
              <div
                data-testid={`feature-row-${feat.id}`}
                className={`p-5 border-b border-white/10 font-body text-sm text-white/70 leading-snug ${
                  rowIdx % 2 === 0 ? "bg-[#0F0F12]/40" : "bg-transparent"
                }`}
              >
                {feat.label}
              </div>
              {["standard", "deluxe", "collector"].map((edKey) => (
                <div
                  key={edKey}
                  data-testid={`feature-cell-${feat.id}-${edKey}`}
                  className={`flex items-center justify-center p-5 border-b border-l border-white/10 ${
                    edKey === "collector" ? "bg-accent/[0.03]" : ""
                  } ${rowIdx % 2 === 0 ? "bg-[#0F0F12]/40" : ""}`}
                >
                  {feat[edKey] ? (
                    <Check size={18} className="text-accent" />
                  ) : (
                    <Minus size={16} className="text-white/20" />
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Mobile cards */}
        <div className="md:hidden space-y-6" data-testid="editions-mobile">
          {editions.map((ed) => (
            <div
              key={ed.id}
              className={`border p-6 ${
                ed.featured
                  ? "border-accent/40 bg-accent/[0.06]"
                  : "border-white/10 bg-[#0F0F12]"
              }`}
            >
              <div className="flex items-center justify-between mb-2">
                <span className="font-heading text-2xl uppercase text-white">
                  {ed.name}
                </span>
                {ed.featured && (
                  <span className="flex items-center gap-1 bg-accent text-black px-2 py-0.5">
                    <Star size={10} />
                    <span className="font-body text-[9px] uppercase tracking-[0.25em] font-bold">
                      Consigliata
                    </span>
                  </span>
                )}
              </div>
              <div
                className={`font-heading text-3xl mb-4 ${
                  ed.featured ? "text-accent" : "text-white"
                }`}
              >
                {ed.price}
              </div>
              <ul className="space-y-2 mb-5">
                {data.features
                  .filter((f) => f[ed.id])
                  .map((f) => (
                    <li
                      key={f.id}
                      className="flex items-start gap-3 font-body text-sm text-white/75 leading-snug"
                    >
                      <Check size={14} className="text-accent mt-0.5 shrink-0" />
                      {f.label}
                    </li>
                  ))}
              </ul>
              <button
                type="button"
                onClick={() =>
                  addItem({
                    id: `edition-${ed.id}`,
                    name: `${ed.name} · The Protocol Lite`,
                    price: ed.price,
                  })
                }
                className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 ${
                  ed.featured
                    ? "bg-accent text-black"
                    : "border border-accent/40 text-accent"
                }`}
              >
                <ShoppingBag size={13} />
                <span className="font-heading text-sm tracking-[0.15em] uppercase">
                  Aggiungi al carrello
                </span>
              </button>
            </div>
          ))}
        </div>

        {data.priceNote && (
          <p className="mt-8 font-body text-[10px] uppercase tracking-[0.3em] text-white/40">
            {data.priceNote}
          </p>
        )}
      </div>
    </section>
  );
}
